import { createTheme } from '@mui/material/styles';
import { nexusTokens } from './theme/tokens';

export { nexusTokens } from './theme/tokens';

export const darkTheme = createTheme({
  nexus: nexusTokens,
  palette: {
    mode: 'dark',
    primary: {
      main: '#58a6ff', // GitHub blue
      light: '#79c0ff',
      dark: '#1f6feb',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f78166', // GitHub orange
      light: '#ff9a7a',
      dark: '#d15704',
      contrastText: '#ffffff',
    },
    background: {
      default: '#0d1117', // GitHub dark background
      paper: '#161b22', // GitHub card background
      elevated: '#21262d', // Slightly lighter for elevated elements
    },
    surface: {
      main: '#161b22',
      light: '#21262d',
      dark: '#0d1117',
    },
    text: {
      primary: '#f0f6fc', // GitHub light text
      secondary: '#8b949e', // GitHub secondary text
      disabled: '#484f58',
    },
    divider: '#30363d', // GitHub border color
    success: {
      main: '#238636', // GitHub green
      light: '#2ea043',
      dark: '#1a7f37',
    },
    warning: {
      main: '#d29922', // GitHub yellow
      light: '#e3b341',
      dark: '#bb8009',
    },
    error: {
      main: '#da3633', // GitHub red
      light: '#f85149',
      dark: '#a40e26',
    },
    info: {
      main: '#58a6ff',
      light: '#79c0ff',
      dark: '#1f6feb',
    },
  },
  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.8125rem',
      lineHeight: 1.6,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.025em',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.3), 0px 1px 3px 1px rgba(0, 0, 0, 0.15)',
    '0px 1px 2px rgba(0, 0, 0, 0.3), 0px 2px 6px 2px rgba(0, 0, 0, 0.15)',
    '0px 4px 8px 3px rgba(0, 0, 0, 0.15), 0px 1px 3px rgba(0, 0, 0, 0.3)',
    '0px 6px 10px 4px rgba(0, 0, 0, 0.15), 0px 2px 4px rgba(0, 0, 0, 0.3)',
    '0px 8px 12px 6px rgba(0, 0, 0, 0.15), 0px 3px 6px rgba(0, 0, 0, 0.3)',
    '0px 10px 14px 8px rgba(0, 0, 0, 0.15), 0px 4px 8px rgba(0, 0, 0, 0.3)',
    '0px 12px 16px 10px rgba(0, 0, 0, 0.15), 0px 5px 10px rgba(0, 0, 0, 0.3)',
    '0px 14px 18px 12px rgba(0, 0, 0, 0.15), 0px 6px 12px rgba(0, 0, 0, 0.3)',
    '0px 16px 20px 14px rgba(0, 0, 0, 0.15), 0px 7px 14px rgba(0, 0, 0, 0.3)',
    '0px 18px 22px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.3)',
    '0px 20px 24px 18px rgba(0, 0, 0, 0.15), 0px 9px 18px rgba(0, 0, 0, 0.3)',
    '0px 22px 26px 20px rgba(0, 0, 0, 0.15), 0px 10px 20px rgba(0, 0, 0, 0.3)',
    '0px 24px 28px 22px rgba(0, 0, 0, 0.15), 0px 11px 22px rgba(0, 0, 0, 0.3)',
    '0px 26px 30px 24px rgba(0, 0, 0, 0.15), 0px 12px 24px rgba(0, 0, 0, 0.3)',
    '0px 28px 32px 26px rgba(0, 0, 0, 0.15), 0px 13px 26px rgba(0, 0, 0, 0.3)',
    '0px 30px 34px 28px rgba(0, 0, 0, 0.15), 0px 14px 28px rgba(0, 0, 0, 0.3)',
    '0px 32px 36px 30px rgba(0, 0, 0, 0.15), 0px 15px 30px rgba(0, 0, 0, 0.3)',
    '0px 34px 38px 32px rgba(0, 0, 0, 0.15), 0px 16px 32px rgba(0, 0, 0, 0.3)',
    '0px 36px 40px 34px rgba(0, 0, 0, 0.15), 0px 17px 34px rgba(0, 0, 0, 0.3)',
    '0px 38px 42px 36px rgba(0, 0, 0, 0.15), 0px 18px 36px rgba(0, 0, 0, 0.3)',
    '0px 40px 44px 38px rgba(0, 0, 0, 0.15), 0px 19px 38px rgba(0, 0, 0, 0.3)',
    '0px 42px 46px 40px rgba(0, 0, 0, 0.15), 0px 20px 40px rgba(0, 0, 0, 0.3)',
    '0px 44px 48px 42px rgba(0, 0, 0, 0.15), 0px 21px 42px rgba(0, 0, 0, 0.3)',
    '0px 46px 50px 44px rgba(0, 0, 0, 0.15), 0px 22px 44px rgba(0, 0, 0, 0.3)',
  ],
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(22, 27, 34, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid #30363d',
          boxShadow:
            '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #161b22 0%, #21262d 100%)',
          border: '1px solid #30363d',
          borderRadius: 8,
          boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          '&:hover': {
            borderColor: '#58a6ff',
            boxShadow:
              '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          textTransform: 'none',
          letterSpacing: '0.025em',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          padding: '10px 24px',
          fontSize: '0.875rem',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 8px 25px rgba(88, 166, 255, 0.3)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #58a6ff 0%, #1f6feb 100%)',
          boxShadow: '0 4px 14px 0 rgba(88, 166, 255, 0.39)',
          '&:hover': {
            background: 'linear-gradient(135deg, #79c0ff 0%, #58a6ff 100%)',
            boxShadow: '0 8px 25px 0 rgba(88, 166, 255, 0.5)',
          },
        },
        outlined: {
          borderColor: '#30363d',
          borderWidth: '2px',
          '&:hover': {
            borderColor: '#58a6ff',
            backgroundColor: 'rgba(88, 166, 255, 0.08)',
            borderWidth: '2px',
          },
        },
        text: {
          '&:hover': {
            backgroundColor: 'rgba(88, 166, 255, 0.08)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(13, 17, 23, 0.6)',
            borderColor: '#30363d',
            borderRadius: 8,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              borderColor: '#58a6ff',
              backgroundColor: 'rgba(13, 17, 23, 0.8)',
            },
            '&.Mui-focused': {
              borderColor: '#58a6ff',
              boxShadow: '0 0 0 3px rgba(88, 166, 255, 0.1)',
              backgroundColor: 'rgba(13, 17, 23, 0.9)',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#8b949e',
            '&.Mui-focused': {
              color: '#58a6ff',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #161b22 0%, #21262d 100%)',
          border: '1px solid #30363d',
          borderRadius: 8,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #161b22 0%, #21262d 100%)',
          border: '1px solid #30363d',
          borderRadius: 8,
          overflow: 'hidden',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #30363d',
          padding: '16px',
        },
        head: {
          backgroundColor: 'rgba(33, 38, 45, 0.8)',
          fontWeight: 600,
          fontSize: '0.875rem',
          color: '#f0f6fc',
        },
        body: {
          fontSize: '0.875rem',
          color: '#c9d1d9',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#21262d',
          border: '1px solid #30363d',
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.75rem',
          '&:hover': {
            backgroundColor: '#30363d',
          },
        },
        label: {
          padding: '0 12px',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #30363d',
        },
        indicator: {
          backgroundColor: '#58a6ff',
          height: '3px',
          borderRadius: '3px 3px 0 0',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          minHeight: '48px',
          '&.Mui-selected': {
            color: '#58a6ff',
          },
          '&:hover': {
            backgroundColor: 'rgba(88, 166, 255, 0.08)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(135deg, #161b22 0%, #21262d 100%)',
          border: '1px solid #30363d',
          borderRadius: 10,
          boxShadow:
            '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(180deg, #161b22 0%, #21262d 100%)',
          borderRight: '1px solid #30363d',
          backdropFilter: 'blur(20px)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(135deg, #161b22 0%, #21262d 100%)',
          border: '1px solid #30363d',
          borderRadius: 12,
          boxShadow:
            '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&:hover': {
            backgroundColor: 'rgba(88, 166, 255, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(88, 166, 255, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(88, 166, 255, 0.16)',
            },
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: '#30363d',
          borderRadius: 4,
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: '#58a6ff',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 44,
          height: 24,
          padding: 0,
        },
        switchBase: {
          padding: 2,
          '&.Mui-checked': {
            transform: 'translateX(20px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
              backgroundColor: '#58a6ff',
              opacity: 1,
            },
          },
        },
        thumb: {
          width: 20,
          height: 20,
        },
        track: {
          borderRadius: 12,
          backgroundColor: '#30363d',
          opacity: 1,
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          color: '#58a6ff',
        },
        track: {
          backgroundColor: '#58a6ff',
        },
        rail: {
          backgroundColor: '#30363d',
        },
        thumb: {
          backgroundColor: '#58a6ff',
          '&:hover': {
            boxShadow: '0 0 0 8px rgba(88, 166, 255, 0.16)',
          },
        },
      },
    },
  },
});
